import React, { useState, useEffect } from 'react';
import random from 'lodash/random';
import { useMutation, useQuery } from 'react-apollo';
import { makeStyles, withStyles, fade } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Button from 'components/common/Button';
import FilledInput from '@material-ui/core/FilledInput';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Pagination from '@material-ui/lab/Pagination';
import SyncIcon from '@material-ui/icons/Sync';
import InputAdornment from '@material-ui/core/InputAdornment';
import SyncDisabledIcon from '@material-ui/icons/SyncDisabled';
import SearchIcon from '@material-ui/icons/Search';
import Loading from 'components/common/Spin';
import ContactCard from './ContactCard';
import {
  GET_CONTACTS,
  GET_PENDING_INVITES,
  UPDATE_CONTACT,
  UPDATE_SUGGESTED
} from 'graphql/referral/contacts';
import useSnack from 'hooks/useSnack';
import {useConfirm } from 'material-ui-confirm';

const NavTab = withStyles((theme) => ({
  root: {
    minWidth: 72,
  },
}))(Tab);

const useStyles = makeStyles((theme) => ({
  searchInput: {
    padding: theme.spacing(2),
    paddingLeft: 0,
  },
  pagination: {
    'margin': 'auto',
    '& .MuiPaginationItem-root': {
      'borderRadius': 4,
      'color': fade(theme.palette.text.secondary, 0.5),
      'fontSize': 14,
      '&.Mui-selected': {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.primary.main,
      },
    },
  },
}));

const Contacts = ({ onInvite, onDisconnect }) => {
  const classes = useStyles();
  const [tab, setTab] = useState('1');
  const [query, setQuery] = useState();
  const [offset, setOffset] = useState(0);
  const [updateContact] = useMutation(UPDATE_CONTACT);
  const [updateSuggested] = useMutation(UPDATE_SUGGESTED);
  const snack = useSnack();
  const confirm = useConfirm();
  const queryVars = {
    limit: tab === '1' ? 4 : 8,
    offset,
    search: query ? `%${query}%` : undefined,
  };

  const { loading, error, data, refetch } = useQuery(
    tab === '1' ? GET_CONTACTS : GET_PENDING_INVITES,
    {
      variables: queryVars,
      fetchPolicy: 'cache-and-network',
    },
  );

  useEffect(() => {
    if (!data || tab === '2') return;
    const { rows } = data;
    if (rows.length) {
      updateSuggested({
        variables: {
          ids: rows.map((i) => i.id),
        },
      });
    }
  }, [data]);

  const count = data?.count.aggregate.count;
  const totalPages = Math.ceil(count / queryVars.limit);
  const currentPage = offset === 0 ? 1 : Math.floor(offset / queryVars.limit) + 1;

  if (error) return error.message;

  const onDelete = (contact) => {
    return updateContact({
      variables: {
        id: contact.id,
        data: {
          deleted: true,
        },
      },
    }).then(
      () => {
        refetch(queryVars);
      },
      (e) => snack.error(e.message),
    );
  };

  const onQueryChange = (e) => {
    setQuery(e.target.value);
    setOffset(0);
  };

  const onPageChange = (e, v) => {
    setOffset(queryVars.limit * (v - 1));
  };

  const onShuffle = () => {
    window.analytics.track('Referral.Shuffle.Clicked');
    setTab('1');
    let randomOffset = random(0, count - queryVars.limit);
    if (randomOffset < 0) {
      randomOffset = 0;
    }
    setOffset(randomOffset);
  };

  const onTabChange = (t) => {
    setTab(t);
    setQuery();
    setOffset(0);
  }

  const handleDisconnect = () => {
    return confirm().then(onDisconnect);
  };

  return (
    <Card component={Box} mb={3}>
      <CardContent>
        <Grid container component={Box} mb={3}>
          <Grid item sm={9}>
            <Typography variant="h5">Sync Contacts</Typography>
            <Typography color="textSecondary">
              Sync your contacts to invite your friends to WeAdmit.
            </Typography>
          </Grid>
          <Grid item sm={3}>
            <Box textAlign="right">
              <Button
                variant="outlined"
                startIcon={<SyncDisabledIcon />}
                color="secondary"
                onClick={handleDisconnect}
              >
                Disconnect
              </Button>
            </Box>
          </Grid>
        </Grid>
        <Grid container component={Box} mb={3}>
          <Grid item sm={7}>
            <Tabs
              indicatorColor="primary"
              textColor="primary"
              value={tab}
              onChange={(e, v) => onTabChange(v)}
            >
              <NavTab label="Synced" value="1" />
              <NavTab label="Invited" value="2" />
            </Tabs>
          </Grid>
          <Grid item sm={5}>
            <Box display="flex">
              <FilledInput
                fullWidth
                disableUnderline
                value={query}
                onChange={onQueryChange}
                classes={{
                  input: classes.searchInput,
                }}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon color="disabled" fontSize="small" />
                  </InputAdornment>
                }
              />
              <Button
                size="large"
                variant="contained"
                color="primary"
                startIcon={<SyncIcon />}
                onClick={onShuffle}
              >
                Shuffle
              </Button>
            </Box>
          </Grid>
        </Grid>
        <Loading spinning={loading}>
          <Grid container spacing={2}>
            {data?.rows.map((contact) => (
              <Grid item xs={12} sm={3} key={contact.id}>
                <ContactCard
                  contact={contact}
                  onInvite={onInvite}
                  onDelete={onDelete}
                />
              </Grid>
            ))}
          </Grid>
          {!data?.rows.length ? (
            <Box p={3} textAlign="center">
              {tab === '1' ? 'No contacts found.' : 'No invites found.'}
            </Box>
          ) : null}
          <Box mt={4}>
            {tab === '1' ? (
              <Box textAlign="center">
                { count ? (
                  <Button
                    onClick={() => {
                      window.analytics.track('Referral.InviteAll.Clicked');
                      onInvite('ALL');
                    }}
                    size="large"
                    variant="contained"
                    color="primary"
                  >
                    Invite all {count} contacts
                  </Button>
                ) : null}
              </Box>
            ) : (
              <Box display="flex">
                <Pagination
                  className={classes.pagination}
                  count={totalPages}
                  page={currentPage}
                  onChange={onPageChange}
                  variant="outlined"
                  shape="rounded"
                />
              </Box>
            )}
          </Box>
        </Loading>
      </CardContent>
    </Card>
  );
};

export default Contacts;
